import { Subject } from 'rxjs';
import { SolitaireDealer } from '../logic/dealer/solitaire-dealer';
import { GameLogicBuilder } from '../logic/game-logic-builder';
import { SolitaireGameLogic } from '../logic/solitaire-game-logic';
import { SolitaireGameRound } from './solitaire-game-round';
import { SolitaireLocalRoundParams } from './solitaire-local-round-params';
import { SolitaireRemoteRoundParams } from './solitaire-remote-round-params';
import { SolitaireListener } from '../game/solitaire-listener';
import { GameState } from 'game-logic';
import { GameRound } from 'src/games/common/phaser/model/game-round';
import { RoundCreator } from 'src/games/common/phaser/model/round-creator';
import { RoundParams } from 'src/games/common/phaser/model/round-params';

export class SolitaireRoundCreator extends RoundCreator {
  public get currentRound(): SolitaireGameRound | undefined {
    return this._currentRound;
  }
  public get onBoardChangedTrigger() {
    return this.onBoardChanged.asObservable();
  }
  public get onGameStateChangedTrigger() {
    return this.onGameStateChanged.asObservable();
  }

  private solitaireListener: SolitaireListener;
  private onBoardChanged: Subject<void>;
  private onGameStateChanged: Subject<GameState>;
  private _currentRound: SolitaireGameRound | undefined;

  constructor(solitaireListener: SolitaireListener) {
    super();

    this.solitaireListener = solitaireListener;
    this.onBoardChanged = new Subject();
    this.onGameStateChanged = new Subject();
  }

  public override startNewRound(roundParams: RoundParams): GameRound {
    let nextRound: SolitaireGameRound;
    if (SolitaireRemoteRoundParams.isRound(roundParams)) {
      nextRound = this.createRemoteRound(roundParams);
    } else if (SolitaireLocalRoundParams.isRound(roundParams)) {
      nextRound = this.createLocalRound(roundParams);
    } else {
      throw new Error('Failed to create new round');
    }

    nextRound.onBoardChangedAction = () => {
      this.onBoardChanged.next();
    };
    nextRound.onGameStateChangedAction = (state) => {
      this.solitaireListener.onGameStateChange(state);

      this.onGameStateChanged.next(state);
    };
    nextRound.onCommandCommittedAction = (moves) => {
      this.solitaireListener.onNewMove(moves);
    };
    nextRound.onScoreChangedAction = (score) => {
      this.solitaireListener.onScoreChange(score);
    };

    this._currentRound = nextRound;
    return nextRound;
  }

  public override createRemoteRoundParams(solitaireListener: SolitaireListener): RoundParams {
    return SolitaireRemoteRoundParams.create(solitaireListener);
  }

  public override createLocalRoundParams(solitaireListener: SolitaireListener): RoundParams {
    return SolitaireLocalRoundParams.create(solitaireListener);
  }

  private createRemoteRound(remoteRoundParams: SolitaireRemoteRoundParams): SolitaireGameRound {
    const gameLogic = SolitaireRoundCreator.createGame(remoteRoundParams.dealer);

    return new SolitaireGameRound(gameLogic, remoteRoundParams);
  }

  private createLocalRound(localRoundParams: SolitaireLocalRoundParams): SolitaireGameRound {
    const gameLogic = SolitaireRoundCreator.createGame(localRoundParams.dealer);

    return new SolitaireGameRound(gameLogic, localRoundParams);
  }

  private static createGame(dealer: SolitaireDealer): SolitaireGameLogic {
    return GameLogicBuilder.newGame(dealer);
  }
}
